import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { CreatorResponseDto } from '@/app/common/general-types/creators.dto';
import {
  RecruitGroupTypesEnum,
  RecruitGroupsDto,
} from '@/app/common/general-types/recruit-groups.dto';
import { CreatorProfileComponent } from '@/app/pages/creators/molecules/creator-profile/creator-profile.component';
import { MagicSearchService } from '@/app/pages/creators/service/magic-search/magic-search.service';
import { CreatorService } from '@/app/services/creator/creator.service';
import { ImageService } from '@/app/services/image/image.service';
import { RecruitGroupService } from '@/app/services/recruit-group/recruit-group.service';
import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  Observable,
  of,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';

type LoadingState = 'idle' | 'loading' | 'loaded' | 'error';

type FocussedBrand = {
  id?: string;
  companyName?: string;
};

const PREVIEW_LIMIT = 10;

@Component({
  selector: 'dbv2-interested-creators',
  imports: [RouterLink, CreatorProfileComponent],
  templateUrl: './interested-creators.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InterestedCreators implements OnDestroy {
  loadingState = signal<LoadingState>('idle');
  interestedCreators = signal<CreatorResponseDto[]>([]);
  brandName = signal('your brand');

  readonly previewCreators = computed(() =>
    this.interestedCreators().slice(0, PREVIEW_LIMIT),
  );
  readonly extraCount = computed(() =>
    Math.max(this.interestedCreators().length - PREVIEW_LIMIT, 0),
  );
  readonly creatorCount = computed(() => this.interestedCreators().length);
  readonly interestedCreatorsQueryParams = {
    group: RecruitGroupTypesEnum.CREATOR_INTEREST,
  };
  readonly heading = computed(() => `Creators interested in ${this.brandName()}`);
  readonly subtitle = computed(() => {
    const count = this.creatorCount();
    const brand = this.brandName();
    if (count === 1) {
      return `There is 1 Creator who has shown interest in working with ${brand}`;
    }
    return `There are ${count} Creators who have shown interest in working with ${brand}`;
  });

  private readonly subscriptionList = new Subscription();

  private readonly recruitGroupService = inject(RecruitGroupService);
  private readonly creatorService = inject(CreatorService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly router = inject(Router);
  readonly imageService = inject(ImageService);
  readonly magicSearchService = inject(MagicSearchService);

  constructor() {
    this.subscriptionList.add(
      this.currentUserService.focussedBrandSubject
        .pipe(
          debounceTime(350),
          distinctUntilChanged(
            (a: FocussedBrand | undefined, b: FocussedBrand | undefined) => a?.id === b?.id,
          ),
          switchMap((brand: FocussedBrand | undefined) => this.loadForBrand(brand)),
        )
        .subscribe(),
    );
  }

  loadForBrand(brand: FocussedBrand | undefined): Observable<CreatorResponseDto[]> {
    if (!brand?.id) {
      this.interestedCreators.set([]);
      this.brandName.set('your brand');
      this.loadingState.set('idle');
      return EMPTY;
    }

    this.brandName.set(brand.companyName || 'your brand');
    this.loadingState.set('loading');

    return this.recruitGroupService
      .getRecruitGroupsByTypeAndBusiness(RecruitGroupTypesEnum.CREATOR_INTEREST, brand.id)
      .pipe(
        switchMap((response: RecruitGroupsDto) => {
          const groupId = response?.data?.[0]?.id;
          if (!groupId) {
            return of([]);
          }
          return this.creatorService.getCreatorsByRecruitmentGroupId(groupId);
        }),
        tap((creators) => {
          this.interestedCreators.set([...creators].reverse());
          this.loadingState.set('loaded');
        }),
        catchError((err: unknown) => {
          console.error('Failed to get interested creators', err);
          this.interestedCreators.set([]);
          this.loadingState.set('error');
          return EMPTY;
        }),
      );
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  creatorDisplayName(creator: CreatorResponseDto): string {
    const initial = creator.lastName?.charAt(0);
    return initial ? `${creator.firstName} ${initial}.` : creator.firstName;
  }

  openCreator(creator: CreatorResponseDto): void {
    this.magicSearchService.openCreatorPortfolio(creator, {
      navItems: this.interestedCreators().map((item) => ({ creator: item })),
    });
  }

  seeAll(): void {
    const creators = this.interestedCreators();
    if (creators.length === 1) {
      this.openCreator(creators[0]);
      return;
    }
    void this.router.navigate(['/creators-my-groups'], {
      queryParams: this.interestedCreatorsQueryParams,
    });
  }
}
